/**
 * Paleta del editor: campos del XML del SII (generados del XSD) y elementos
 * gráficos. Se arrastran al lienzo, o se agregan con clic o Enter.
 */
import { useMemo, useState } from 'react'
import {
  Barcode, ChevronDown, ChevronRight, FileSignature, Hash, Image as ImageIcon, ImagePlus, List, Minus, QrCode, Square, Table,
  Type, WholeWord,
} from 'lucide-react'
import { SegmentGroup } from '@/components/ds/SegmentGroup'
import { SelectField } from '@/components/ds/SelectField'
import { SearchInput } from '@/components/ds/SearchInput'
import {
  usePrintTemplatePalette,
  type NodoDePaleta,
  type PreviewVariant,
} from '@/modules/config/hooks/usePrintTemplates'
import { TIPO_DE_ARRASTRE, type CampoArrastrado, type TipoElemento } from './geometria'

const VARIANTES: { value: PreviewVariant; label: string }[] = [
  { value: 'documento', label: 'Factura y otros' },
  { value: 'boleta', label: 'Boleta' },
  { value: 'exportacion', label: 'Exportación' },
  { value: 'liquidacion', label: 'Liquidación' },
]
const nombreVariante = (v: PreviewVariant) => VARIANTES.find(x => x.value === v)?.label ?? v

export type ElementoDePaleta = TipoElemento | 'monto_en_palabras' | 'acuse_de_recibo'

const ELEMENTOS: { tipo: ElementoDePaleta; label: string; icon: typeof Type; hint: string }[] = [
  { tipo: 'text', label: 'Texto', icon: Type, hint: 'Texto fijo o con campos: «N° {Encabezado/IdDoc/Folio}»' },
  { tipo: 'field_list', label: 'Lista de datos', icon: List, hint: 'Rótulo y valor por fila (receptor, totales); las filas vacías no se imprimen y las de abajo suben' },
  { tipo: 'monto_en_palabras', label: 'Monto en palabras', icon: WholeWord, hint: 'Se calcula del total del documento: «SON: CIENTO DIECINUEVE MIL PESOS»' },
  { tipo: 'rect', label: 'Recuadro', icon: Square, hint: 'Recuadro con borde y fondo' },
  { tipo: 'line', label: 'Línea', icon: Minus, hint: 'Horizontal o vertical' },
  { tipo: 'image', label: 'Imagen', icon: ImagePlus, hint: 'Un logo o una imagen de la biblioteca' },
  { tipo: 'detail_table', label: 'Tabla de detalle', icon: Table, hint: 'Las líneas del documento; salta de página' },
  { tipo: 'references_table', label: 'Referencias', icon: Table, hint: 'Documentos referenciados' },
  { tipo: 'timbre', label: 'Timbre', icon: ImageIcon, hint: 'PDF417 del SII con la leyenda de la resolución' },
  { tipo: 'barcode', label: 'Código de barras', icon: Barcode, hint: 'Code 128 o Code 39, con texto o campos' },
  { tipo: 'qr', label: 'Código QR', icon: QrCode, hint: 'Con texto o campos' },
  { tipo: 'acuse_de_recibo', label: 'Acuse de recibo', icon: FileSignature, hint: 'El recuadro de la Ley 19.983 que firma quien recibe, con «CEDIBLE». Solo en la copia cedible' },
]

function coincide(nodo: NodoDePaleta, filtro: string): boolean {
  if (!filtro) return true
  const f = filtro.toLowerCase()
  if (nodo.name.toLowerCase().includes(f) || nodo.description.toLowerCase().includes(f)) return true
  return (nodo.children ?? []).some(h => coincide(h, filtro))
}

function Rama({ nodo, prefijo, lista, filtro, onAgregarCampo, nivel }: {
  nodo: NodoDePaleta
  prefijo: string
  lista: string | null
  filtro: string
  nivel: number
  onAgregarCampo: (campo: CampoArrastrado) => void
}) {
  const [abierta, setAbierta] = useState(nivel === 0 && (nodo.name === 'Encabezado' || nodo.name === 'Detalle'))
  if (!coincide(nodo, filtro)) return null
  const completa = prefijo + nodo.path

  if (nodo.children) {
    const prefijoHijos = nodo.repeatable ? `${completa}/` : prefijo
    const listaHijos = nodo.repeatable ? completa : lista
    const visible = abierta || !!filtro
    return (
      <li>
        <button type="button" onClick={() => setAbierta(v => !v)} title={nodo.description}
          className="flex items-center gap-1 w-full text-left px-1.5 py-1 rounded text-xs font-medium text-content hover:bg-soft">
          {visible ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {nodo.name}
          {nodo.repeatable && <span className="ml-auto text-[10px] text-content-subtle">se repite</span>}
        </button>
        {visible && (
          <ul className="ml-3 border-l border-divider pl-1">
            {nodo.children.map(h => (
              <Rama key={h.path} nodo={h} prefijo={prefijoHijos} lista={listaHijos} filtro={filtro}
                nivel={nivel + 1} onAgregarCampo={onAgregarCampo} />
            ))}
          </ul>
        )}
      </li>
    )
  }

  const campo: CampoArrastrado = { ruta: completa, lista, rutaEnLaLista: nodo.path }
  const falta = nodo.missing_in ?? []
  return (
    <li>
      <button type="button" draggable
        onDragStart={e => { e.dataTransfer.setData(TIPO_DE_ARRASTRE.campo, JSON.stringify(campo)); e.dataTransfer.effectAllowed = 'copy' }}
        onClick={() => onAgregarCampo(campo)}
        title={`${nodo.description}${nodo.values?.length ? `\n${nodo.values.length} valores posibles` : ''}\n${completa}`}
        className="flex items-center gap-1.5 w-full text-left px-1.5 py-1 rounded text-xs text-content-muted hover:bg-soft cursor-grab active:cursor-grabbing">
        <Hash size={11} className="shrink-0 text-content-subtle" />
        <span className="truncate">{nodo.name}</span>
        {falta.length > 0 && (
          <span className="ml-auto shrink-0 text-[10px] px-1 rounded bg-warning-surface text-warning-content"
            title={`No existe en: ${falta.map(nombreVariante).join(', ')}. Ahí se imprime vacío.`}>
            falta en {falta.length === 1 ? nombreVariante(falta[0]) : falta.length}
          </span>
        )}
      </button>
    </li>
  )
}

export function Paleta({ variante, onVariante, tipos, onAgregarCampo, onAgregarElemento }: {
  variante: PreviewVariant
  onVariante: (v: PreviewVariant) => void
  tipos: number[]
  onAgregarCampo: (campo: CampoArrastrado) => void
  onAgregarElemento: (tipo: ElementoDePaleta) => void
}) {
  const [pestana, setPestana] = useState<'campos' | 'elementos'>('campos')
  const [filtro, setFiltro] = useState('')
  const { data, isLoading } = usePrintTemplatePalette(variante, tipos)
  const arbol = useMemo(() => data?.tree ?? [], [data])

  return (
    <div className="flex flex-col gap-2 min-h-0 h-full">
      <SegmentGroup label="Paleta" size="sm" value={pestana} onChange={setPestana}
        options={[{ value: 'campos', label: 'Campos' }, { value: 'elementos', label: 'Elementos' }]} />
      {pestana === 'campos' ? (
        <>
          <SelectField dense label="Esquema del SII" value={variante} onChange={e => onVariante(e.target.value as PreviewVariant)}
            hint="Los datos de ejemplo del lienzo salen de este esquema.">
            {VARIANTES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
          </SelectField>
          <SearchInput value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Buscar campo" label="Buscar campo" />
          <ul className="flex-1 min-h-0 overflow-y-auto pr-1">
            {isLoading && <li className="text-xs text-content-subtle p-2">Cargando campos…</li>}
            {arbol.map(n => (
              <Rama key={n.path} nodo={n} prefijo="" lista={null} filtro={filtro} nivel={0} onAgregarCampo={onAgregarCampo} />
            ))}
          </ul>
          <p className="text-[11px] text-content-subtle">Arrastrar al lienzo, o a una tabla para agregarlo como columna.</p>
        </>
      ) : (
        <ul className="space-y-1">
          {ELEMENTOS.map(({ tipo, label, icon: Icono, hint }) => (
            <li key={tipo}>
              <button type="button" draggable title={hint}
                onDragStart={e => { e.dataTransfer.setData(TIPO_DE_ARRASTRE.elemento, tipo); e.dataTransfer.effectAllowed = 'copy' }}
                onClick={() => onAgregarElemento(tipo)}
                className="flex items-center gap-2 w-full text-left px-2 py-2 rounded-lg border border-divider bg-surface text-sm text-content hover:bg-soft cursor-grab active:cursor-grabbing">
                <Icono size={14} className="text-content-subtle" />
                {label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
